import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Btn from '../../../../../components/Buttons/Btn';
import AnimatedLoadingIcon from '../../../../../components/LoadingOverlay/AnimatedLoadingIcon';
import Modal from '../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../components/Notification';
import HttpStatus from '../../../../../enums/httpStatus';
import {
  databaseManagementExportAsFileApi,
  databaseManagementDownloadLatestExportApi,
  tempFilesPaginatedApi,
} from '../../../../../services/requests';

type DatabaseManagementTable = {
  label: string;
  value: string;
};

type LatestExportResponse = {
  uuid: string;
  url: string;
  expires_at?: string | null;
  original_name?: string | null;
  size?: number | null;
  mime_type?: string | null;
};

type TempFileListItem = {
  uuid: string;
  created_at?: string | null;
  createdAt?: string | null;
};

type LatestExportInfo = LatestExportResponse & {
  created_at?: string | null;
};

type Props = {
  showModal: boolean;
  closeModal: () => void;
  table: DatabaseManagementTable;
};

const formatBytes = (value?: number | null): string => {
  if (!value || value <= 0) return '-';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const base = 1024;
  const exponent = Math.min(Math.floor(Math.log(value) / Math.log(base)), units.length - 1);

  const size = value / Math.pow(base, exponent);
  const formatted = exponent === 0 ? `${Math.round(size)}` : size.toFixed(2);

  return `${formatted} ${units[exponent]}`;
};

const extractErrorMessage = (err: unknown): string | null => {
  const axiosErr = err as AxiosError<any>;
  if (axiosErr?.response?.data?.message) return String(axiosErr.response.data.message);

  const thrownResponse = err as any;
  if (thrownResponse?.data?.message) return String(thrownResponse.data.message);

  if (thrownResponse?.message) return String(thrownResponse.message);

  return null;
};

const DownloadLatestExportModal: React.FC<Props> = ({ showModal, closeModal, table }) => {
  const { t } = useTranslation();

  const [isFetchingLatest, setIsFetchingLatest] = useState(false);
  const [isQueuingExport, setIsQueuingExport] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [hasFetchedLatestOnce, setHasFetchedLatestOnce] = useState(false);
  const [backendMessage, setBackendMessage] = useState<string | null>(null);
  const [latestExport, setLatestExport] = useState<LatestExportInfo | null>(null);

  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isQueuingExportRef = useRef(false);
  const isDownloadingRef = useRef(false);
  const openedAtMsRef = useRef<number | null>(null);
  const baselineUuidRef = useRef<string | null>(null);
  const baselineCreatedAtMsRef = useRef<number | null>(null);

  const clearPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const toMs = (value?: string | null): number | null => {
    if (!value) return null;
    const parsed = dayjs(value);
    if (!parsed.isValid()) return null;
    return parsed.valueOf();
  };

  const createdAtFormatted = useMemo(() => {
    const createdAt = latestExport?.created_at;
    if (!createdAt) return null;

    const parsed = dayjs(createdAt);
    if (!parsed.isValid()) return null;

    return parsed.format('DD/MM/YYYY HH:mm');
  }, [latestExport?.created_at]);

  const fetchLatestExport = async (): Promise<{
    exportInfo: LatestExportInfo | null;
    message: string | null;
  }> => {
    try {
      databaseManagementDownloadLatestExportApi.query = '';
      const response = await databaseManagementDownloadLatestExportApi.show(table.value);

      if (response.status === HttpStatus.NOT_FOUND || !response.data?.data?.uuid) {
        return {
          exportInfo: null,
          message: response.data?.message ?? null,
        };
      }

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      const base: LatestExportResponse = response.data.data;

      let createdAt: string | null | undefined = null;
      try {
        tempFilesPaginatedApi.query = '';
        const tempFilesResponse = await tempFilesPaginatedApi.get({
          search: base.uuid,
          page: 1,
          quantityPerPage: 1,
        });

        if (
          tempFilesResponse.status === HttpStatus.OK &&
          Array.isArray(tempFilesResponse.data?.data)
        ) {
          const item = (tempFilesResponse.data.data[0] ?? null) as TempFileListItem | null;
          createdAt = item?.created_at ?? item?.createdAt ?? null;
        }
      } catch (e) {
        // best-effort: keep going without created_at
      }

      return {
        exportInfo: { ...base, created_at: createdAt },
        message: null,
      };
    } catch (err) {
      if (!databaseManagementDownloadLatestExportApi.axios.isCancel(err)) {
        const axiosErr = err as AxiosError<any, any>;
        const extracted = extractErrorMessage(err);
        const message: string = extracted ?? String(t('errorTryingToDownloadFile'));

        if (axiosErr?.response) backendErrorNotification(axiosErr);
        else frontendNotification({ message, type: 'error' });

        return {
          exportInfo: null,
          message,
        };
      }

      return { exportInfo: null, message: null };
    } finally {
    }
  };

  const refresh = async () => {
    try {
      setIsFetchingLatest(true);
      setBackendMessage(null);
      setLatestExport(null);

      const { exportInfo, message } = await fetchLatestExport();

      setLatestExport(exportInfo);
      setBackendMessage(message);
    } finally {
      setIsFetchingLatest(false);
      setHasFetchedLatestOnce(true);
    }
  };

  const stopProcessing = () => {
    clearPolling();
    setIsProcessing(false);
  };

  const checkForNewExport = async () => {
    const { exportInfo } = await fetchLatestExport();
    if (!exportInfo) return;

    const openedAtMs = openedAtMsRef.current;
    const baselineUuid = baselineUuidRef.current;
    const baselineCreatedAtMs = baselineCreatedAtMsRef.current;
    const currentCreatedAtMs = toMs(exportInfo.created_at);

    const hasBaseline = Boolean(baselineUuid || baselineCreatedAtMs);

    let isNew = false;
    if (!hasBaseline) {
      isNew = true;
    } else if (currentCreatedAtMs && openedAtMs && currentCreatedAtMs > openedAtMs) {
      isNew = true;
    } else if (
      currentCreatedAtMs && baselineCreatedAtMs && currentCreatedAtMs > baselineCreatedAtMs
    ) {
      isNew = true;
    } else if (baselineUuid && exportInfo.uuid && exportInfo.uuid !== baselineUuid) {
      isNew = true;
    }

    if (!isNew) return;

    setLatestExport(exportInfo);
    setBackendMessage(null);
    stopProcessing();
  };

  const handleGenerateNewExport = async () => {
    if (isQueuingExportRef.current || isFetchingLatest || isProcessing) return;

    try {
      isQueuingExportRef.current = true;
      setIsQueuingExport(true);
      setBackendMessage(null);

      databaseManagementExportAsFileApi.query = '';
      const response = await databaseManagementExportAsFileApi.get({
        tableName: table.value,
        fileType: 'csv',
        separatorType: 'semicolon',
      });

      if (response.status !== HttpStatus.OK) {
        throw response;
      }

      frontendNotification({
        message: response.data?.message ?? t('exportQueued'),
        type: 'success',
      });

      openedAtMsRef.current = Date.now();
      baselineUuidRef.current = latestExport?.uuid ?? null;
      baselineCreatedAtMsRef.current = toMs(latestExport?.created_at) ?? null;

      setIsProcessing(true);
      clearPolling();

      await checkForNewExport();
      pollIntervalRef.current = setInterval(() => {
        checkForNewExport();
      }, 5000);
    } catch (err) {
      if (!databaseManagementExportAsFileApi.axios.isCancel(err)) {
        const axiosErr = err as AxiosError<any, any>;
        if (axiosErr?.response) {
          backendErrorNotification(axiosErr);
        } else {
          const extracted = extractErrorMessage(err);
          const message: string = extracted ?? String(t('errorTryingToDownloadFile'));
          frontendNotification({ message, type: 'error' });
        }
      }
    } finally {
      isQueuingExportRef.current = false;
      setIsQueuingExport(false);
    }
  };

  useEffect(() => {
    if (!showModal) return;
    refresh();
  }, [showModal]);

  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, []);

  const headerText = useMemo(() => {
    if (!createdAtFormatted) return null;
    return t('latestExportFoundAt', { date: createdAtFormatted });
  }, [createdAtFormatted, t]);

  const handleDownload = () => {
    const url = latestExport?.url;
    if (!url || isDownloadingRef.current) return;

    try {
      isDownloadingRef.current = true;
      setIsDownloading(true);

      const link = document.createElement('a');
      link.href = url;
      link.rel = 'noopener noreferrer';

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      closeModal();
    } catch (err) {
      isDownloadingRef.current = false;
      setIsDownloading(false);

      const extracted = extractErrorMessage(err);
      frontendNotification({
        message: extracted ?? String(t('errorTryingToDownloadFile')),
        type: 'error',
      });
    }
  };

  const hintText = useMemo(() => {
    if (!createdAtFormatted) return null;
    return t('latestExportUpdateHint', {
      date: createdAtFormatted,
      generateAction: t('generateNewFile'),
      downloadAction: t('downloadLatestProcessedFile'),
    });
  }, [createdAtFormatted, t]);

  const disableGenerate = isFetchingLatest || isQueuingExport || isProcessing || isDownloading;
  const disableDownload = !latestExport?.url ||
    isFetchingLatest || isQueuingExport || isProcessing || isDownloading;

  return (
    <Modal
      title={`${t('downloadLatestExport')} - ${t(table.label)}`}
      showModal={showModal}
      closeModal={closeModal}
      cancelButton={closeModal}
      hideActionButton
      bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem' }}
    >
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        margin: '1rem'
      }}>
        <div
          style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
          }}
        >
          <Btn
            text={t('generateNewFile')}
            onClick={handleGenerateNewExport}
            disabled={disableGenerate}
            style={{ maxWidth: '260px' }}
          />

          <Btn
            text={t('downloadLatestProcessedFile')}
            onClick={handleDownload}
            disabled={disableDownload}
            style={{ maxWidth: '260px' }}
          />
        </div>

        {isProcessing && (
          <div style={{ padding: '1.25rem 0' }}>
            <div style={{ fontWeight: 800, marginBottom: '6px' }}>{t('processing')}</div>
            <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>
              {t('processingFilePleaseWaitOrComeBackLater')}
            </div>

            <div style={{ marginTop: '16px', display: 'flex', justifyContent: 'center' }}>
              <AnimatedLoadingIcon />
            </div>
          </div>
        )}

        {!isProcessing && isFetchingLatest && (
          <div style={{ padding: '1rem 0' }}>
            <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('loading')}...</div>
            <div style={{ opacity: 0.85, fontSize: '0.95rem' }}>{t('loadingLatestExport')}</div>
          </div>
        )}

        {!isProcessing && !isFetchingLatest && latestExport && (
          <>
            {headerText && (
              <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{headerText}</div>
            )}

            <div
              style={{
                border: '1px solid rgba(0,0,0,0.08)',
                borderRadius: '10px',
                padding: '12px',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '10px' }}>{t('fileInformation')}</div>

              <div style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '8px 12px' }}>
                <div style={{ opacity: 0.85 }}>{t('originalName')}</div>
                <div style={{ wordBreak: 'break-word' }}>{latestExport.original_name ?? '-'}</div>

                <div style={{ opacity: 0.85 }}>{t('mimeType')}</div>
                <div style={{ wordBreak: 'break-word' }}>{latestExport.mime_type ?? '-'}</div>

                <div style={{ opacity: 0.85 }}>{t('size')}</div>
                <div>{formatBytes(latestExport.size)}</div>

                <div style={{ opacity: 0.85 }}>{t('createdAt')}</div>
                <div style={{ fontWeight: 800 }}>{createdAtFormatted ?? '-'}</div>
              </div>
            </div>

            <div style={{ opacity: 0.9, fontSize: '0.95rem' }}>
              {hintText}
            </div>

            {backendMessage && (
              <div
                style={{
                  borderLeft: '4px solid rgba(0,0,0,0.12)',
                  padding: '10px 12px',
                  background: 'rgba(0,0,0,0.02)',
                  borderRadius: '8px',
                }}
              >
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('message')}</div>
                <div style={{ opacity: 0.9 }}>{backendMessage}</div>
              </div>
            )}
          </>
        )}

        {!isProcessing && !isFetchingLatest && !latestExport && backendMessage && (
          <>
            <div style={{ fontWeight: 800, fontSize: '1.05rem' }}>{t('latestExportNotFound')}</div>

            <div
              style={{
                borderLeft: '4px solid rgba(0,0,0,0.12)',
                padding: '10px 12px',
                background: 'rgba(0,0,0,0.02)',
                borderRadius: '8px',
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: '6px' }}>{t('message')}</div>
              <div style={{ opacity: 0.9 }}>{backendMessage}</div>
            </div>
          </>
        )}

        {!isProcessing && !isFetchingLatest && !latestExport && !backendMessage && (
          <div style={{ opacity: 0.9 }}>
            {hasFetchedLatestOnce ? t('latestExportNotFound') : t('loadingLatestExport')}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default DownloadLatestExportModal;
